import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { unwrap, type ObjectResponse } from './envelope';

/**
 * Token bundle the auth slice persists. Mirrors the response shape from
 * `/auth/login` and `/auth/refresh` (defined in `auth.service.ts`).
 *
 * `tenantCode` was added 2026-05-16 because the live refresh endpoint
 * requires it in the request body (see §25). The login response
 * already returns it as `tenant.code`, so capture it at login time
 * and thread it through subsequent refresh calls.
 */
export interface ClientAuthSession {
  accessToken: string;
  refreshToken: string;
  /** Absolute ISO datetime when accessToken expires. */
  expiresAt: string;
  tenantId: string;
  /** Short tenant code (e.g. "bill"). Required by /api/Auth/refresh. */
  tenantCode?: string;
  sessionId: string;
}

export type SessionGetter = () => ClientAuthSession | null | undefined;
export type SessionSetter = (session: ClientAuthSession) => void;
export type UnauthorizedHandler = () => void;

export interface ApiClientOptions {
  baseURL?: string;
  /** Returns the active session (or null when logged-out). */
  getSession?: SessionGetter;
  /** Persists a refreshed session back to the store. */
  setSession?: SessionSetter;
  /** Called when refresh is impossible (no refresh token, refresh failed, etc.). */
  onUnauthorized?: UnauthorizedHandler;
  /** Proactive-refresh window in seconds. Default 120 (2 min). */
  refreshLeewaySeconds?: number;
}

/* ------------------------------------------------------------------------ */
/* Internal helpers                                                         */
/* ------------------------------------------------------------------------ */

function isExpiringSoon(expiresAt: string, leewaySec: number): boolean {
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return false;
  return t - Date.now() < leewaySec * 1000;
}

function isExpired(expiresAt: string): boolean {
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return false;
  return t <= Date.now();
}

interface RetryFlag {
  _retry?: boolean;
  _skipAuth?: boolean;
}
type RetryConfig = InternalAxiosRequestConfig & RetryFlag;

/* ------------------------------------------------------------------------ */
/* Client factory                                                           */
/* ------------------------------------------------------------------------ */

export function createApiClient(opts: ApiClientOptions = {}): AxiosInstance {
  const leeway = opts.refreshLeewaySeconds ?? 120;
  const instance = axios.create({
    baseURL: opts.baseURL ?? '/api',
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
  });

  // Single-flight refresh — concurrent requests share the same in-flight promise.
  let refreshPromise: Promise<ClientAuthSession> | null = null;

  /**
   * Calls the live `/api/Auth/refresh` endpoint with the **tenant code,
   * session id and refresh token** triplet. The live API requires
   * `tenantCode` in the body (verified 2026-05-16) — sending without
   * it returns `400 The TenantCode field is required.`
   *
   * Path note: the canonical live URL is `/Auth/refresh` (no `-token`
   * suffix). The previous code called `/Auth/refresh-token` which
   * returned 404. Tracked as §25 in BACKEND_AGENT_PROMPTS.md — we use
   * the live URL today; the backend may add the `-token` alias later
   * for consistency with the §16 naming convention.
   *
   * Response is flat (not enveloped) per §16 / §25 — `unwrap()` handles
   * both shapes so this works whichever the backend ships.
   */
  async function performRefresh(
    currentRefreshToken: string,
    sessionId: string,
    tenantCode?: string,
  ): Promise<ClientAuthSession> {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        const resp = await axios.post<ObjectResponse<ClientAuthSession>>(
          (opts.baseURL ?? '/api').replace(/\/$/, '') + '/Auth/refresh',
          { tenantCode, refreshToken: currentRefreshToken, sessionId },
          { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
        );
        const next = unwrap(resp.data);
        opts.setSession?.(next);
        return next;
      } finally {
        // Always clear so the next 401 can trigger a new refresh.
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  }

  instance.interceptors.request.use(async (config) => {
    const cfg = config as RetryConfig;
    if (cfg._skipAuth) return config;

    const session = opts.getSession?.();
    if (!session?.accessToken) return config;

    // Proactive refresh: if access token expires inside the leeway window,
    // refresh first so the outgoing request carries the fresh token.
    let token = session.accessToken;
    if (
      session.refreshToken &&
      session.sessionId &&
      isExpiringSoon(session.expiresAt, leeway)
    ) {
      try {
        const next = await performRefresh(session.refreshToken, session.sessionId, session.tenantCode);
        token = next.accessToken;
      } catch {
        // Fall through with the stale token; the response interceptor will
        // catch the 401 and trigger logout. Don't break the request here.
      }
    }

    if (config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (r) => r,
    async (err: AxiosError) => {
      const status = err?.response?.status;
      const original = err?.config as RetryConfig | undefined;

      // Reactive refresh on 401 — exactly one retry, then bail to logout.
      if (status === 401 && original && !original._retry && !original._skipAuth) {
        const session = opts.getSession?.();
        if (session?.refreshToken && session.sessionId) {
          original._retry = true;
          try {
            const next = await performRefresh(session.refreshToken, session.sessionId, session.tenantCode);
            original.headers = original.headers ?? {};
            (original.headers as Record<string, string>).Authorization =
              `Bearer ${next.accessToken}`;
            return instance.request(original);
          } catch {
            opts.onUnauthorized?.();
            return Promise.reject(err);
          }
        }
        opts.onUnauthorized?.();
      }

      return Promise.reject(err);
    }
  );

  return instance;
}

/* ------------------------------------------------------------------------ */
/* Shared singleton client                                                   */
/* ------------------------------------------------------------------------ */

let _getSession: SessionGetter = () => null;
let _setSession: SessionSetter = () => {};
let _onUnauthorized: UnauthorizedHandler = () => {};
let _refreshLeewaySeconds = 120;

/**
 * Same-origin base. Both DEV and production use a relative `/api` so the
 * browser never makes a cross-origin call:
 *
 * - DEV: Vite reverse-proxies `/api/*` to the live host
 *   (see `apps/{web,panel}/vite.config.ts`).
 * - PROD: IIS reverse-proxies `/api/*` to the live host
 *   (see `apps/web/public/web.config` rule `ApiReverseProxy`).
 *
 * Set `VITE_API_BASE_URL` to override (e.g. when the API and frontend
 * are deployed on the same host, point this at `/api` explicitly; or
 * for a backend that *does* return CORS headers, point at the absolute
 * URL).
 */
const DEFAULT_BASE_URL = '/api';

function resolveEnvBaseURL(): string {
  try {
    const env = (import.meta as unknown as {
      env?: Record<string, string | boolean | undefined>;
    }).env;
    if (env?.VITE_API_BASE_URL) return String(env.VITE_API_BASE_URL);
    return DEFAULT_BASE_URL;
  } catch {
    return DEFAULT_BASE_URL;
  }
}

/**
 * The shared API client used by all singleton services exported from `@kss/api`.
 * Call {@link configureApi} once at app startup to wire it to the Redux store.
 *
 * Per spec: `Authorization: Bearer {token}` is auto-attached on every request;
 * **TenantId is never sent manually** (the backend reads it from the JWT claims).
 */
export const apiClient: AxiosInstance = createApiClient({
  baseURL: resolveEnvBaseURL(),
  getSession: () => _getSession(),
  setSession: (s) => _setSession(s),
  onUnauthorized: () => _onUnauthorized(),
  refreshLeewaySeconds: _refreshLeewaySeconds,
});

export interface ConfigureApiOptions {
  baseURL?: string;
  getSession?: SessionGetter;
  setSession?: SessionSetter;
  onUnauthorized?: UnauthorizedHandler;
  refreshLeewaySeconds?: number;
}

export function configureApi(opts: ConfigureApiOptions): void {
  if (opts.getSession) _getSession = opts.getSession;
  if (opts.setSession) _setSession = opts.setSession;
  if (opts.onUnauthorized) _onUnauthorized = opts.onUnauthorized;
  if (opts.refreshLeewaySeconds != null) _refreshLeewaySeconds = opts.refreshLeewaySeconds;
  if (opts.baseURL) apiClient.defaults.baseURL = opts.baseURL;

  // Expose a slim `window.__kssApi` bridge so @kss/ui components (which
  // can't import from @kss/api without a circular dep) can still hit the
  // live backend. The shape is intentionally narrow — get / put / post —
  // plus `getToken()` for SSE/WebSocket flows that can't carry a Bearer
  // header (EventSource's spec doesn't let you set request headers, so
  // the JWT has to ride as `?access_token=<jwt>` instead).
  if (typeof window !== 'undefined') {
    (window as Window & {
      __kssApi?: {
        get(path: string, config?: unknown): Promise<{ data: unknown }>;
        put(path: string, body: unknown): Promise<{ data: unknown }>;
        post(path: string, body: unknown): Promise<{ data: unknown }>;
        getToken(): string | null;
        baseURL(): string;
      };
    }).__kssApi = {
      get: (p, c) => apiClient.get(p, c as AxiosRequestConfig | undefined).then((r) => ({ data: r.data })),
      put: (p, b) => apiClient.put(p, b).then((r) => ({ data: r.data })),
      post: (p, b) => apiClient.post(p, b).then((r) => ({ data: r.data })),
      getToken: () => _getSession()?.accessToken ?? null,
      baseURL: () => (apiClient.defaults.baseURL ?? '/api'),
    };
  }
}

export { isExpired, isExpiringSoon };
export type { AxiosInstance, AxiosRequestConfig };
